import { apiFetch } from "../apiFetch";
import { getApiBase } from "../config";

const apiBase = getApiBase();

// ─── Types ────────────────────────────────────────────

export interface JurisdictionSummary {
  id: number;
  state_code: string;
  rule_count?: number;
  rules?: Record<string, any>;
  updated_at: string | null;
}

export interface JurisdictionDetail {
  id: number;
  state_code: string;
  rules: Record<string, any>;
  updated_at: string | null;
}

export interface GlobalTemplate {
  id: string;
  org_id: string | null;
  jurisdiction_code: string;
  doc_type: string | null;
  fingerprint_hash: string;
  fingerprint_version: number;
  split_rules: any[];
  redaction_zones: any[];
  is_active: boolean;
  updated_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// ─── Public endpoints (all users) ─────────────────────

export const lawLibraryApi = {
  listJurisdictions: async (): Promise<{ jurisdictions: JurisdictionSummary[] }> => {
    return apiFetch(`${apiBase}/law-library/jurisdictions`);
  },

  getJurisdiction: async (stateCode: string): Promise<JurisdictionDetail> => {
    return apiFetch(`${apiBase}/law-library/jurisdictions/${stateCode}`);
  },

  listGlobalTemplates: async (params: {
    page?: number;
    per_page?: number;
    jurisdiction_code?: string;
  } = {}): Promise<{ templates: GlobalTemplate[]; pagination: PaginationMeta }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.jurisdiction_code) qs.set("jurisdiction_code", params.jurisdiction_code);
    const q = qs.toString();
    return apiFetch(`${apiBase}/law-library/global-templates${q ? `?${q}` : ""}`);
  },
};

// ─── Admin endpoints (SUPERADMIN) ─────────────────────

export const adminLawLibraryApi = {
  listJurisdictions: async (): Promise<{ jurisdictions: JurisdictionSummary[] }> => {
    return apiFetch(`${apiBase}/admin/law-library/jurisdictions`);
  },

  createJurisdiction: async (stateCode: string, rules: Record<string, any>): Promise<JurisdictionDetail> => {
    return apiFetch(`${apiBase}/admin/law-library/jurisdictions`, {
      method: "POST",
      body: JSON.stringify({ state_code: stateCode, rules }),
    });
  },

  updateJurisdiction: async (stateCode: string, rules: Record<string, any>): Promise<JurisdictionDetail> => {
    return apiFetch(`${apiBase}/admin/law-library/jurisdictions/${stateCode}`, {
      method: "PUT",
      body: JSON.stringify({ rules }),
    });
  },

  deleteJurisdiction: async (stateCode: string): Promise<{ status: string }> => {
    return apiFetch(`${apiBase}/admin/law-library/jurisdictions/${stateCode}`, {
      method: "DELETE",
    });
  },

  listGlobalTemplates: async (params: {
    page?: number;
    per_page?: number;
    jurisdiction_code?: string;
  } = {}): Promise<{ templates: GlobalTemplate[]; pagination: PaginationMeta }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.jurisdiction_code) qs.set("jurisdiction_code", params.jurisdiction_code);
    const q = qs.toString();
    return apiFetch(`${apiBase}/admin/law-library/global-templates${q ? `?${q}` : ""}`);
  },

  listAllTemplates: async (params: {
    page?: number;
    per_page?: number;
    org_id?: string;
    scope?: "global" | "org";
  } = {}): Promise<{ templates: GlobalTemplate[]; pagination: PaginationMeta }> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.org_id) qs.set("org_id", params.org_id);
    if (params.scope) qs.set("scope", params.scope);
    const q = qs.toString();
    return apiFetch(`${apiBase}/admin/law-library/all-templates${q ? `?${q}` : ""}`);
  },

  updateGlobalTemplate: async (templateId: string, body: Record<string, any>): Promise<GlobalTemplate> => {
    return apiFetch(`${apiBase}/admin/law-library/global-templates/${templateId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  promoteToGlobal: async (templateId: string): Promise<{ status: string; template: GlobalTemplate }> => {
    return apiFetch(`${apiBase}/admin/law-library/promote`, {
      method: "POST",
      body: JSON.stringify({ template_id: templateId }),
    });
  },

  deactivateGlobalTemplate: async (templateId: string): Promise<{ status: string }> => {
    return apiFetch(`${apiBase}/admin/law-library/global-templates/${templateId}`, {
      method: "DELETE",
    });
  },
};
